import React, { createContext, useContext, useState, useCallback } from "react";
import preBoardingDocumentService from "../services/preBoardingDocumentService";
import { useToast } from "@chakra-ui/react";

const PreBoardingContext = createContext();

export const usePreBoarding = () => {
  const context = useContext(PreBoardingContext);
  if (!context) {
    throw new Error("usePreBoarding must be used within PreBoardingProvider");
  }
  return context;
};

export const PreBoardingProvider = ({ children }) => {
  const toast = useToast();

  // State management
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [submissionSummary, setSummissionSummary] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Document Upload Operations
  const uploadDocument = useCallback(
    async (formData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.uploadDocument(
          formData
        );

        toast({
          title: "Success",
          description: "Document uploaded successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to upload document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const uploadMultipleDocuments = useCallback(
    async (formData) => {
      try {
        setLoading(true);
        setError(null);
        const response =
          await preBoardingDocumentService.uploadMultipleDocuments(formData);

        toast({
          title: "Success",
          description: `${
            response.data.uploaded?.length || 0
          } documents uploaded successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to upload documents";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // Document Retrieval Operations
  const getCandidateDocuments = useCallback(
    async (candidateId, filters = {}) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.getCandidateDocuments(
          candidateId,
          filters
        );
        setDocuments(response.data.documents || []);
        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to fetch documents";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const getSubmissionSummary = useCallback(async (candidateId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getSubmissionSummary(
        candidateId
      );
      setSummissionSummary(response.data.summary);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch submission summary";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDocument = useCallback(async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getDocument(documentId);
      setSelectedDocument(response.data.document);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch document";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPendingDocuments = useCallback(async (candidateId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getPendingDocuments(
        candidateId
      );
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch pending documents";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDocumentsByCategory = useCallback(async (candidateId, category) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getDocumentsByCategory(
        candidateId,
        category
      );
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch documents by category";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDocumentStatistics = useCallback(async (candidateId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getDocumentStatistics(
        candidateId
      );
      setStatistics(response.data.statistics);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch statistics";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadDocument = useCallback(
    async (documentId) => {
      try {
        setLoading(true);
        setError(null);
        const blob = await preBoardingDocumentService.downloadDocument(
          documentId
        );

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = selectedDocument?.originalFileName || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Document downloaded successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to download document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [selectedDocument, toast]
  );

  const updateDocument = useCallback(
    async (documentId, updateData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.updateDocument(
          documentId,
          updateData
        );

        toast({
          title: "Success",
          description: "Document updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to update document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const deleteDocument = useCallback(
    async (documentId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.deleteDocument(
          documentId
        );

        toast({
          title: "Success",
          description: "Document deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Remove from local state
        setDocuments((prev) => prev.filter((doc) => doc._id !== documentId));

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to delete document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const requestReUpload = useCallback(
    async (documentId, reason) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.requestReUpload(
          documentId,
          { reason }
        );

        toast({
          title: "Success",
          description: "Re-upload requested successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to request re-upload";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const getRequiredDocuments = useCallback(async (role) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getRequiredDocuments(
        role
      );
      setRequiredDocuments(response.data.requirements || []);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch required documents";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Document Verification Operations (HR)
  const assignDocumentForReview = useCallback(
    async (documentId, reviewerData) => {
      try {
        setLoading(true);
        setError(null);
        const response =
          await preBoardingDocumentService.assignDocumentForReview(
            documentId,
            reviewerData
          );

        toast({
          title: "Success",
          description: "Document assigned for review",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to assign document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const startReview = useCallback(
    async (verificationId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.startReview(
          verificationId
        );

        toast({
          title: "Success",
          description: "Review started",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to start review";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const addComment = useCallback(
    async (verificationId, commentData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.addComment(
          verificationId,
          commentData
        );

        toast({
          title: "Success",
          description: "Comment added",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to add comment";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const approveDocument = useCallback(
    async (verificationId, approvalData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.approveDocument(
          verificationId,
          approvalData
        );

        toast({
          title: "Success",
          description: "Document approved",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to approve document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const rejectDocument = useCallback(
    async (verificationId, rejectionData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.rejectDocument(
          verificationId,
          rejectionData
        );

        toast({
          title: "Success",
          description: "Document rejected",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to reject document";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const requestVerificationReUpload = useCallback(
    async (verificationId, reUploadData) => {
      try {
        setLoading(true);
        setError(null);
        const response =
          await preBoardingDocumentService.requestVerificationReUpload(
            verificationId,
            reUploadData
          );

        toast({
          title: "Success",
          description: "Re-upload requested",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to request re-upload";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const approveConditional = useCallback(
    async (verificationId, conditionalData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.approveConditional(
          verificationId,
          conditionalData
        );

        toast({
          title: "Success",
          description: "Conditional approval granted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to approve conditionally";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const getVerificationDetails = useCallback(async (verificationId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getVerificationDetails(
        verificationId
      );
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch verification details";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getMyPendingReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getMyPendingReviews();
      setVerifications(response.data.verifications || []);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch pending reviews";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCandidateVerifications = useCallback(async (candidateId) => {
    try {
      setLoading(true);
      setError(null);
      const response =
        await preBoardingDocumentService.getCandidateVerifications(candidateId);
      setVerifications(response.data.verifications || []);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch verifications";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getVerificationSummary = useCallback(async (candidateId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getVerificationSummary(
        candidateId
      );
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch verification summary";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const completeCondition = useCallback(
    async (verificationId, conditionIndex, completionData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.completeCondition(
          verificationId,
          conditionIndex,
          completionData
        );

        toast({
          title: "Success",
          description: "Condition completed",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to complete condition";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // Reminder Operations
  const getCandidateReminders = useCallback(async (candidateId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getCandidateReminders(
        candidateId
      );
      setReminders(response.data.data || []);
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch reminders";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const acknowledgeReminder = useCallback(
    async (reminderId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.acknowledgeReminder(
          reminderId
        );

        toast({
          title: "Success",
          description: "Reminder acknowledged",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to acknowledge reminder";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  const getEscalatedReminders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await preBoardingDocumentService.getEscalatedReminders();
      return response.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to fetch escalated reminders";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const escalateReminder = useCallback(
    async (reminderId, reason) => {
      try {
        setLoading(true);
        setError(null);
        const response = await preBoardingDocumentService.escalateReminder(
          reminderId,
          { reason }
        );

        toast({
          title: "Success",
          description: "Reminder escalated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        return response.data;
      } catch (err) {
        const errorMsg =
          err.response?.data?.message || "Failed to escalate reminder";
        setError(errorMsg);
        toast({
          title: "Error",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // Clear operations
  const clearDocuments = useCallback(() => {
    setDocuments([]);
  }, []);

  const clearSelectedDocument = useCallback(() => {
    setSelectedDocument(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    // State
    documents,
    selectedDocument,
    submissionSummary,
    verifications,
    reminders,
    requiredDocuments,
    statistics,
    loading,
    error,

    // Document Operations
    uploadDocument,
    uploadMultipleDocuments,
    getCandidateDocuments,
    getSubmissionSummary,
    getDocument,
    getPendingDocuments,
    getDocumentsByCategory,
    getDocumentStatistics,
    downloadDocument,
    updateDocument,
    deleteDocument,
    requestReUpload,
    getRequiredDocuments,

    // Verification Operations
    assignDocumentForReview,
    startReview,
    addComment,
    approveDocument,
    rejectDocument,
    requestVerificationReUpload,
    approveConditional,
    getVerificationDetails,
    getMyPendingReviews,
    getCandidateVerifications,
    getVerificationSummary,
    completeCondition,

    // Reminder Operations
    getCandidateReminders,
    acknowledgeReminder,
    getEscalatedReminders,
    escalateReminder,

    // Clear Operations
    clearDocuments,
    clearSelectedDocument,
    clearError,
  };

  return (
    <PreBoardingContext.Provider value={value}>
      {children}
    </PreBoardingContext.Provider>
  );
};

export default PreBoardingContext;
